import { LoaderCircleIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Outlet } from 'react-router'

import { type Allowances, getDefaultAllowance } from 'components/quoting/quotes/flat_allowances/FlatAllowanceExplorer'
import { QuotePricingProvider } from 'components/quoting/quotes/pricing/QuotePricingProvider'

import { useFlags } from '../FlagProvider'
import { useQuote } from '../QuoteProvider'
import { convertSavedAllowancesToFormFormat, isDynamicAllowance } from './allowanceUtils'
import { SelfServeExplorerContext, SelfServeExplorerProvider } from './QuoteSelfServeExplorerContext'
import { useProspect } from '../setup/ProspectProvider'

export { SelfServeExplorerContext as QuoteSelfServeExplorerContext }

export function QuoteSelfServeExplorerProvider () {
  const { quote, refetch } = useQuote()
  const { prospect } = useProspect()
  const { flags } = useFlags()
  const [startTime, setStartTime] = useState(0)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  useEffect(() => {
    const loadFreshData = async () => {
      await refetch()
      setIsInitialLoad(false)
    }
    loadFreshData()
  }, [refetch])

  useEffect(() => {
    if (quote && (quote.status !== 'processed' && quote.status !== 'failed')) {
      setStartTime(Date.now())
      const interval = setInterval(() => {
        refetch()
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [quote?.status, refetch])

  // Use suggested allowance from prospect if available and flag is enabled, otherwise use default
  const initialAllowance = useMemo(() => {
    const defaultAllowance = getDefaultAllowance()
    const suggestedAmount = prospect?.suggested_allowance_amount

    if (flags.prefillAllowanceFromSurvey && suggestedAmount) {
      return {
        ...defaultAllowance,
        flat_config: {
          ...defaultAllowance.flat_config,
          employee_amount: suggestedAmount,
          spouse_amount: suggestedAmount,
        },
      }
    }

    return defaultAllowance
  }, [flags.prefillAllowanceFromSurvey, prospect?.suggested_allowance_amount])

  const methods = useForm<Allowances>({
    defaultValues: {
      allowances: [initialAllowance],
    },
    mode: 'onChange',
  })

  useEffect(() => {
    if (quote?.allowances && quote.allowances.length > 0) {
      const savedAllowances = quote.allowances

      if (!isDynamicAllowance(savedAllowances)) {
        const convertedAllowances = convertSavedAllowancesToFormFormat(savedAllowances)
        methods.reset({ allowances: convertedAllowances })
      }
    }
  }, [quote?.allowances, methods])

  if (!quote) {
    return (
      <div className='flex grow items-center justify-center'>
        <LoaderCircleIcon size={24} className='animate-spin text-gray-500' />
      </div>
    )
  }

  if (quote.status === 'failed') {
    return (
      <div className='flex grow flex-col items-center justify-center space-y-4'>
        <h2 className='heading-lg'>Oh no, we seem to be having trouble creating your allowances!</h2>
        <p className='body'>
          Our team has been notified, but you can <a href='mailto:support@thatch.com'>reach out to us</a> for help if you need support sooner.
        </p>
      </div>
    )
  } else if (quote.status !== 'processed' || isInitialLoad) {
    const message = Date.now() - startTime > 3000
      ? 'We\'re still crunching numbers, almost there!'
      : 'Sit tight! This may take a few seconds...'

    return (
      <div className='flex grow flex-col items-center justify-center space-y-4'>
        <LoaderCircleIcon size={32} className='animate-spin text-gray-500' />
        <div className='text-center space-y-2'>
          <h2 className='heading-lg'>Calculating the best strategy for you and your team</h2>
          <p className='body'>{message}</p>
        </div>
      </div>
    )
  }

  return (
    <FormProvider {...methods}>
      <QuotePricingProvider quoteId={quote.id} workflowType='self_serve' defaultQuoteType={quote.quote_type}>
        <SelfServeExplorerProvider>
          <Outlet />
        </SelfServeExplorerProvider>
      </QuotePricingProvider>
    </FormProvider>
  )
}
